import { TranslateService } from '@ngx-translate/core';
import { RECEIVE_TIME, RECEIVE_TIME_SC } from 'src/app/common/const';
import { Operator } from 'src/app/shared/model/operator';
import { RequestCriteria } from '../../shared/model/request-criteria';
import { stringToVisibleDateTimeFormat } from '../../shared/util/date-util';
import * as moment from 'moment';
import { toVisibleDateTimeFormat } from 'src/app/shared/util/date-util';

const MAX_LINE_LENGTH = 120;
const IGNORE_CRITERION_FIELD_NAMES = ['stopPile.carrier.id', 'reverse_doors_order'];

export function createPassengerReportFilename(reportNameKey: string, correctedReportsDataType: boolean | undefined, filters: RequestCriteria,
                                              translate: TranslateService): string {
  let baseName: string;
  if (correctedReportsDataType === true)
    baseName = translate.instant(reportNameKey) + ' - ' + translate.instant('correctedData');
  else if (correctedReportsDataType === false)
    baseName = translate.instant(reportNameKey) + ' - ' + translate.instant('rawData');
  else
    baseName = translate.instant(reportNameKey);
  baseName += ' - ' + toVisibleDateTimeFormat(moment());
  return generateReportTitle(filters, baseName, translate);
}

export function createChartTitle(reportNameKey: string, filters: RequestCriteria, correctedReportsDataType: boolean | undefined, translate: TranslateService):
  string {
  let dataTypeText = '';
  if (correctedReportsDataType === true)
    dataTypeText = ', ' + translate.instant('correctedData');
  if (correctedReportsDataType === false)
    dataTypeText = ', ' + translate.instant('rawData');
  const baseName = translate.instant(reportNameKey) + dataTypeText;
  return generateReportTitle(filters, baseName, translate, true);
}

export function generateReportTitle(filters: RequestCriteria, baseName: string, translate: TranslateService, splitLongLines: boolean = false): string {
  const texts: string[] = [baseName];

  const criteria = filters.criteria.filter(criterion => criterion.fieldName != RECEIVE_TIME && criterion.fieldName != RECEIVE_TIME_SC);
  criteria.forEach(criterion => {
    let valuesList = [];
    let fieldName: string;
    let operator: string;
    if (criterion.operator == Operator.or && criterion.criteria) {
      criterion.criteria.forEach(insideCriterion => {
        valuesList.push(createCriterionValueText(insideCriterion));
        fieldName = insideCriterion.fieldName;
        operator = insideCriterion.operator;
      });
    } else {
      valuesList.push(createCriterionValueText(criterion));
      fieldName = criterion.fieldName;
      operator = criterion.operator;
    }

    operator = getOperatorTranslation(operator, translate);
    if (operator.length > 0) operator += ' ';
    valuesList = valuesList.filter(value => value !== null);
    if (valuesList.length && !IGNORE_CRITERION_FIELD_NAMES.includes(fieldName)) {
      if (fieldName.includes('.'))
        fieldName = fieldName.slice(fieldName.lastIndexOf('.'));
      texts.push(`${translate.instant(fieldName)}: ${operator}${valuesList.join(', ')}`);
    }
  });

  const receiveTimeFilter = filters.criteria.find(criterion => criterion.fieldName == RECEIVE_TIME || criterion.fieldName == RECEIVE_TIME_SC);
  if (receiveTimeFilter) {
    texts.push(`${translate.instant(RECEIVE_TIME)}: ${createDateFilterValue(receiveTimeFilter)}`);
  }

  if (splitLongLines) {
    const lines: string[] = [];
    texts.forEach(text => {
      if (lines.length) {
        const lastLineLength = lines[lines.length - 1].length;
        if (lastLineLength > MAX_LINE_LENGTH || lastLineLength + text.length > MAX_LINE_LENGTH) {
          lines[lines.length - 1] = lines[lines.length - 1] + ',';
          lines.push(text);
        } else {
          lines[lines.length - 1] = `${lines[lines.length - 1]}, ${text}`;
        }
      } else {
        lines.push(text);
      }
    });
    return lines.join('<br>');
  } else {
    return texts.join(', ');
  }
}

function createDateFilterValue(field: RequestCriteria): string {
  const start = stringToVisibleDateTimeFormat(field.start);
  const end = stringToVisibleDateTimeFormat(field.end);
  return `${start} - ${end}`;
}

function createCriterionValueText(criterion: RequestCriteria): string | null {
  if (criterion.start !== undefined && criterion.end !== undefined)
    return createDateFilterValue(criterion);
  else if (criterion.value !== undefined)
    return criterion.value;
}

function getOperatorTranslation(operator: string, translate: TranslateService): string {
  switch (operator) {
    case Operator.equals:
    case Operator.iEquals:
    case Operator.between:
    case Operator.betweenInclusive:
      return '';
    case Operator.notEqual:
    case Operator.iNotEqual:
      return translate.instant('notEqual');
    case Operator.contains:
    case Operator.iContains:
      return translate.instant('contains');
    case Operator.notContains:
    case Operator.iNotContains:
      return translate.instant('notContains');
    case Operator.endsWith:
    case Operator.iEndsWith:
      return translate.instant('endsWithShort');
    case Operator.startsWith:
    case Operator.iStartsWith:
      return translate.instant('startsWithShort');
    case Operator.isNull:
      return translate.instant('isNull');
    case Operator.notNull:
      return translate.instant('notNull');
    case Operator.greaterThan:
      return '>';
    case Operator.greaterOrEqual:
      return '>=';
    case Operator.lessThan:
      return '<';
    case Operator.lessOrEqual:
      return '=<';
  }
  return operator;
}
